'use client'

import type { ReactNode } from 'react'
import { createContext, useContext } from 'react'

import type { NotificationView } from '@package/notification'

/**
 * Where a card navigates. Modals are routes so push and deep links can target them.
 *
 * A `route` may carry `search` because some destinations are a path plus parameters
 * rather than a path alone — `/belajar/t/misi` identifies its subject entirely through
 * `?type=&id=`. The resolver must not concatenate those onto `to` itself: the caller
 * appends its own `from` / `back_to`, and a `to` that already contains a `?` produces a
 * second one and a dead link.
 */
export type NotificationTarget =
  /**
   * `search` carries route state a path alone cannot express, e.g. which dashboard tab to open.
   *
   * `organizationId` names the organization the destination resolves in, for pages that read
   * their subject through the session's active organization (a child's profile is
   * `getChildDetails`, scoped to it). The feed is per user, not per organization, so the card may
   * be opened while a different organization is active. The navigator that receives this target
   * must switch to that organization before pushing — see `useSwitchToNotificationOrganization`
   * — and, when this app cannot host it, hand the URL to the app that can. Absent: navigate as is.
   */
  | { kind: 'route'; to: string; search?: Record<string, string>; organizationId?: string }
  | { kind: 'modal'; to: string; search: Record<string, string> }
  | { kind: 'none' }

/** Maps a notification to a target. Each app supplies its own entity-type -> route/modal registry. */
export type NotificationResolver = (notification: NotificationView) => NotificationTarget

const NotificationResolverContext = createContext<NotificationResolver | null>(null)

export interface NotificationResolverProviderProps {
  resolver: NotificationResolver
  children: ReactNode
}

/**
 * Provider that makes an app-specific notification resolver available to shared components.
 * Each app (web, school, and later a mobile client) registers its own entity-type -> route/modal
 * mapping and passes it here, keeping `@ui/shared` router-agnostic.
 *
 * @example
 * ```tsx
 * <NotificationResolverProvider resolver={resolveNotificationTarget}>
 *   {children}
 * </NotificationResolverProvider>
 * ```
 */
export function NotificationResolverProvider({ resolver, children }: NotificationResolverProviderProps) {
  return <NotificationResolverContext.Provider value={resolver}>{children}</NotificationResolverContext.Provider>
}

/**
 * Hook to access the notification resolver from context.
 *
 * Falls back to an inert resolver (always `{ kind: 'none' }`) when no provider is present,
 * so a missing provider degrades a notification card to non-clickable instead of crashing.
 */
export function useNotificationResolver(): NotificationResolver {
  const resolver = useContext(NotificationResolverContext)
  return resolver ?? (() => ({ kind: 'none' }))
}
